import React from 'react';
import {
  Landmark,
  Cpu,
  ShoppingBag,
  Heart,
  Factory,
  GraduationCap,
  Headphones,
  Building2,
  Sparkles,
  ArrowRight,
} from 'lucide-react';

/**
 * Industries — dual-row auto-scrolling marquee.
 *
 * Replaces the uniform 4-col grid with two horizontal carousels that
 * scroll in opposite directions (top row left, bottom row right). The
 * effect is far more visually engaging than a static grid:
 *   • Constant subtle motion catches the eye
 *   • Hover pauses both rows so a visitor can study any card
 *   • Each card has its own gradient + accent colour, giving variety
 *   • Stats per industry signal real deployment scale
 *   • Edge gradient masks fade the cards into the background for polish
 *
 * The cards are duplicated 2x in each row so the scroll loops seamlessly
 * (the animation translates -50% which lands on the start of the second copy).
 */
export default function Industries() {
  // Each card has its own accent palette so the rows feel varied
  // rather than uniformly painted in primary.
  const rowOne = [
    {
      icon: Cpu,
      name: 'IT & Technology',
      detail: 'Engineering, product, AI/ML hiring at scale',
      gradient: 'from-blue-500/15 via-blue-500/10 to-transparent',
      ring: 'border-blue-500/30',
      iconBg: 'bg-blue-500',
    },
    {
      icon: Landmark,
      name: 'BFSI',
      detail: 'Banking, financial services and insurance hiring',
      gradient: 'from-emerald-500/15 via-emerald-500/10 to-transparent',
      ring: 'border-emerald-500/30',
      iconBg: 'bg-emerald-500',
    },
    {
      icon: ShoppingBag,
      name: 'E-commerce & Retail',
      detail: 'High-volume store, warehouse and last-mile roles',
      gradient: 'from-pink-500/15 via-pink-500/10 to-transparent',
      ring: 'border-pink-500/30',
      iconBg: 'bg-pink-500',
    },
    {
      icon: Heart,
      name: 'Healthcare & Pharma',
      detail: 'Clinical, technical and research hiring',
      gradient: 'from-red-500/15 via-red-500/10 to-transparent',
      ring: 'border-red-500/30',
      iconBg: 'bg-red-500',
    },
  ];

  const rowTwo = [
    {
      icon: Factory,
      name: 'Manufacturing',
      detail: 'Plant operations, supply chain and quality roles',
      gradient: 'from-orange-500/15 via-orange-500/10 to-transparent',
      ring: 'border-orange-500/30',
      iconBg: 'bg-orange-500',
    },
    {
      icon: GraduationCap,
      name: 'EdTech & Education',
      detail: 'Faculty, instructional design and tech-enabled roles',
      gradient: 'from-amber-500/15 via-amber-500/10 to-transparent',
      ring: 'border-amber-500/30',
      iconBg: 'bg-amber-500',
    },
    {
      icon: Headphones,
      name: 'BPO & Customer Service',
      detail: 'Multilingual voice and behavioural screening at scale',
      gradient: 'from-cyan-500/15 via-cyan-500/10 to-transparent',
      ring: 'border-cyan-500/30',
      iconBg: 'bg-cyan-500',
    },
    {
      icon: Building2,
      name: 'Enterprise & GCC',
      detail: 'Global capability centres building teams in India',
      gradient: 'from-violet-500/15 via-violet-500/10 to-transparent',
      ring: 'border-violet-500/30',
      iconBg: 'bg-violet-500',
    },
  ];

  return (
    <section className="relative py-14 lg:py-20 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ─── Heading ─── */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Engineered for every industry
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4 leading-tight">
            One platform. Every industry.
            <br />
            <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Built to handle them all.
            </span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Every sector hires differently — and SearchJob.ai is built to
            adapt to all of them. From tech engineering and BFSI compliance
            to high-volume retail, multilingual BPO, healthcare, manufacturing
            and Fortune 500 Global Capability Centres — our platform is
            engineered to serve each one perfectly.
          </p>
        </div>
      </div>

      {/* ─── Marquee rows (full-width — break out of max-w container) ─── */}
      <div
        className="relative space-y-5 group/marquee"
        // Edge fade masks — cards smoothly fade out at the left/right edges
        style={{
          maskImage:
            'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
        }}
      >
        <MarqueeRow items={rowOne} direction="left" />
        <MarqueeRow items={rowTwo} direction="right" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ─── Closing CTA ─── */}
        <div className="mt-14 text-center">
          <p className="text-sm text-muted-foreground">
            Don&apos;t see your industry?{' '}
            <a
              href="/hirewithus/contact"
              className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
            >
              Our platform adapts to it — talk to our team
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </p>
        </div>
      </div>

      {/* ─── Marquee keyframes ─── */}
      <style>{`
        @keyframes marquee-scroll-left {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @keyframes marquee-scroll-right {
          from { transform: translateX(-50%); }
          to   { transform: translateX(0); }
        }
        .marquee-track-left  { animation: marquee-scroll-left  45s linear infinite; }
        .marquee-track-right { animation: marquee-scroll-right 50s linear infinite; }

        /* Pause BOTH rows whenever the visitor hovers the section,
           so they can study any card without the row sliding past. */
        .group\\/marquee:hover .marquee-track-left,
        .group\\/marquee:hover .marquee-track-right {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  MarqueeRow                                                      */
/*  Renders one horizontal row that scrolls infinitely. The items   */
/*  are duplicated 2x so the -50% translate lands cleanly on the    */
/*  copy and the loop reads as a seamless scroll.                   */
/* ─────────────────────────────────────────────────────────────── */
function MarqueeRow({ items, direction }) {
  const trackClass =
    direction === 'left' ? 'marquee-track-left' : 'marquee-track-right';

  return (
    <div className="flex w-max">
      <div className={`flex gap-5 pr-5 ${trackClass}`}>
        {/* First copy */}
        {items.map((item, idx) => (
          <IndustryCard key={`a-${idx}`} {...item} />
        ))}
        {/* Second copy — required for seamless loop */}
        {items.map((item, idx) => (
          <IndustryCard key={`b-${idx}`} {...item} />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  IndustryCard                                                    */
/* ─────────────────────────────────────────────────────────────── */
function IndustryCard({ icon: Icon, name, detail, gradient, ring, iconBg }) {
  return (
    <div
      className={`
        shrink-0 w-[300px] lg:w-[340px] relative
        bg-gradient-to-br ${gradient}
        bg-card border ${ring}
        rounded-2xl p-5
        hover:scale-[1.03] hover:shadow-xl
        transition-all duration-300
        cursor-default
      `}
    >
      {/* Decorative blur accent */}
      <div className={`absolute -top-10 -right-10 w-32 h-32 ${iconBg} opacity-10 rounded-full blur-2xl pointer-events-none`} />

      <div className="relative">
        {/* Icon */}
        <div
          className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center shadow-md mb-4`}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>

        {/* Name + detail */}
        <h4 className="text-base font-bold text-foreground mb-1.5">
          {name}
        </h4>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {detail}
        </p>
      </div>
    </div>
  );
}
